/**
 * useThinkingScroll
 *
 * For a clamped, overflowing thinking block: gently eases the content down to
 * the bottom once it overflows (a single calm reveal — no bouncing), then
 * leaves it. The viewport is user-scrollable, so the reader can scroll back up
 * to re-read; it only auto-follows new content while the reader is already at
 * the bottom (the standard "stick to bottom" pattern). Honours
 * prefers-reduced-motion by not auto-scrolling (the box stays scrollable).
 *
 * Why a reveal rather than a live tail: the backend persists a step's prose all
 * at once on completion, so there's nothing to continuously follow — the motion
 * is a one-time "scroll down to show there's more", which the user can undo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const OVERFLOW_THRESHOLD_PX = 6;
const STICK_THRESHOLD_PX = 24;
const REVEAL_SPEED_PX_PER_SEC = 120;
const MIN_REVEAL_MS = 350;
const MAX_REVEAL_MS = 1400;

interface ThinkingScroll {
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** True once the content is taller than the clamp — gate fades on this. */
  overflowing: boolean;
  onScroll: () => void;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function useThinkingScroll(
  enabled: boolean,
  contentKey: string
): ThinkingScroll {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  // Whether to keep following the bottom. Starts true (so the first overflow
  // reveals downward); flips false when the reader scrolls up.
  const stickRef = useRef(true);
  // Guards the onScroll handler from reacting to our own programmatic scroll.
  const programmaticRef = useRef(false);

  // Measure overflow on mount, when the content changes, and on resize.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      setOverflowing(el.scrollHeight - el.clientHeight > OVERFLOW_THRESHOLD_PX);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const child = el.firstElementChild;
    if (child) {
      ro.observe(child);
    }
    return () => ro.disconnect();
  }, [contentKey]);

  // One-time smooth reveal to the bottom when overflowing — only while the
  // reader is "stuck" to the bottom (true on first appearance, false once they
  // scroll up). Re-runs if the content grows.
  useEffect(() => {
    const el = containerRef.current;
    if (!enabled || !overflowing || !el || !stickRef.current) {
      return;
    }
    if (prefersReducedMotion()) {
      return; // box stays scrollable; just don't animate
    }

    const start = el.scrollTop;
    const target = el.scrollHeight - el.clientHeight;
    const distance = target - start;
    if (distance <= 1) {
      return;
    }
    const duration = Math.min(
      MAX_REVEAL_MS,
      Math.max(MIN_REVEAL_MS, (distance / REVEAL_SPEED_PX_PER_SEC) * 1000)
    );

    let rafId = 0;
    let clearGuardId = 0;
    let startTs: number | null = null;
    const step = (ts: number) => {
      if (startTs === null) {
        startTs = ts;
      }
      const progress = Math.min((ts - startTs) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      // Keep the guard raised for the WHOLE animation. The scroll event from a
      // `scrollTop` write is dispatched asynchronously (after this frame), so
      // clearing the guard on the same line — as we used to — let that trailing
      // event reach onScroll while the guard was already down, misreading our own
      // reveal as a user scroll. Lower it one tick after the final frame, once the
      // last scroll event has been delivered.
      programmaticRef.current = true;
      el.scrollTop = start + distance * eased;
      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        clearGuardId = window.setTimeout(() => {
          programmaticRef.current = false;
        }, 0);
      }
    };
    rafId = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(clearGuardId);
      programmaticRef.current = false;
    };
  }, [enabled, overflowing, contentKey]);

  // Track whether the reader is at the bottom. Ignore our own programmatic
  // scrolls so the reveal doesn't flip the stick flag off mid-animation.
  const onScroll = useCallback(() => {
    if (programmaticRef.current) {
      return;
    }
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    stickRef.current = distanceFromBottom <= STICK_THRESHOLD_PX;
  }, []);

  return { containerRef, overflowing, onScroll };
}
